/**
 * FileUploadProgress Component
 * Displays upload progress for files with status indicators
 */

import type { UploadedFile } from "@/stores/ChatStore";

import React from "react";
import { Progress, Chip, Card, CardBody, Button } from "@heroui/react";
import { useTranslation } from "react-i18next";

interface FileUploadProgressProps {
  files: UploadedFile[];
  uploadProgress?: number;
  onRemoveFile?: (fileName: string) => void;
  onRetry?: () => void;
  showRetryButton?: boolean;
  className?: string;
}

export const FileUploadProgress: React.FC<FileUploadProgressProps> = ({
  files,
  uploadProgress = 0,
  onRemoveFile,
  onRetry,
  showRetryButton = false,
  className = "",
}) => {
  const { t } = useTranslation();

  if (files.length === 0 && !showRetryButton) {
    return null;
  }

  const getStatusColor = (status?: string) => {
    if (!status) return "default";

    const lowerStatus = status.toLowerCase();

    if (lowerStatus.includes("upload")) return "primary";
    if (lowerStatus.includes("process")) return "secondary";
    if (lowerStatus.includes("complete") || lowerStatus.includes("success")) return "success";
    if (lowerStatus.includes("fail") || lowerStatus.includes("error")) return "danger";

    return "default";
  };

  const isUploading = files.some(
    (f) =>
      f.status?.toLowerCase().includes("upload") || f.status?.toLowerCase().includes("process"),
  );

  return (
    <div
      aria-label={t("fileUploadSection", "File upload section")}
      className={`space-y-2 ${className}`}
      role="region"
    >
      {files.map((file, index) => (
        <Card
          key={file.id || file.name}
          aria-label={`${t("file", "File")} ${index + 1}: ${file.name}`}
          className="w-full"
          role="article"
        >
          <CardBody className="p-3">
            <div className="flex items-center justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p
                    aria-label={`${t("fileName", "File name")}: ${file.name}`}
                    className="text-sm font-medium truncate"
                    title={file.name}
                  >
                    {file.name}
                  </p>
                  {file.status && (
                    <Chip
                      aria-label={`${t("status", "Status")}: ${file.status}`}
                      color={getStatusColor(file.status)}
                      role="status"
                      size="sm"
                      variant="flat"
                    >
                      {file.status}
                    </Chip>
                  )}
                </div>
                {file.size && (
                  <p
                    aria-label={`${t("fileSize", "File size")}: ${formatFileSize(parseInt(file.size))}`}
                    className="text-xs text-default-500"
                  >
                    {formatFileSize(parseInt(file.size))}
                  </p>
                )}
              </div>

              {onRemoveFile && !isUploading && (
                <Button
                  aria-label={`${t("removeFile", "Remove file")} ${file.name}`}
                  color="danger"
                  data-testid={`remove-${file.name}`}
                  size="sm"
                  variant="light"
                  onClick={() => onRemoveFile(file.name)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      onRemoveFile(file.name);
                    }
                  }}
                >
                  <span aria-hidden="true">✕</span>
                  <span className="sr-only">{t("remove", "Remove")}</span>
                </Button>
              )}
            </div>
          </CardBody>
        </Card>
      ))}

      {isUploading && uploadProgress > 0 && (
        <Progress
          aria-label={t("uploadProgress", "Upload progress")}
          aria-live="polite"
          aria-valuemax={100}
          aria-valuemin={0}
          aria-valuenow={uploadProgress}
          className="w-full"
          color="primary"
          data-testid="upload-progress-bar"
          role="progressbar"
          showValueLabel={true}
          size="sm"
          value={uploadProgress}
        />
      )}

      {showRetryButton && onRetry && (
        <Button
          aria-label={t("retryFailedUploads", "Retry failed uploads")}
          className="w-full"
          color="warning"
          data-testid="retry-upload-button"
          size="sm"
          variant="flat"
          onClick={onRetry}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              onRetry();
            }
          }}
        >
          {t("retryUpload", "Retry Upload")}
        </Button>
      )}
    </div>
  );
};

/**
 * Format file size in human-readable format
 */
function formatFileSize(bytes: number): string {
  if (bytes === 0) return "0 Bytes";

  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
}
